import {useEffect, useState, useMemo} from "react";
import {GraphRepository} from "../../../repositories/GraphRepository";
import GraphView from "./components/graph-view";
import {Box, Stack} from "@mui/material";
import {CreateFabButton} from "./components/CreateFabButton";
import {CreateSchemaModal} from "./components/CreateSchemaModal";
import {CreateNodeModal} from "./components/CreateNodeModal";
import {CreateRelationModal} from "./components/CreateRelationModal";
import {NodeInfoPanel} from "./components/NodeInfoPanel";
import {GraphSearchFilters} from "./components/GraphSearchFilters";
import {NodeLegendPanel} from "./components/NodeLegendPanel";
import {nodeColors} from "../../../utils/neo4j";


export const Neo4jPage = ({project}) => {
  const [nodes, setNodes] = useState([]);
  const [relationship, setRelationship] = useState([]);
  const [triples, setTriples] = useState([]);

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedPredicate, setSelectedPredicate] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  const [openSchemaModal, setOpenSchemaModal] = useState(false);
  const [openNodeModal, setOpenNodeModal] = useState(false);
  const [openRelationModal, setOpenRelationModal] = useState(false);

  // Node visibility state - all nodes visible by default
  const [visibleNodes, setVisibleNodes] = useState({});

  const handleCreateSchema = () => {
    setOpenSchemaModal(true);
  };

  const handleSchemaSuccess = () => {
    // Refresh graph data after schema creation
    fetchGraphData();
  };

  const fetchGraphData = async () => {
    try {
      const nodesResponse = await GraphRepository.getAllNodeLabels();
      setNodes(nodesResponse.data.node_labels || []);

      const relationshipsResponse = await GraphRepository.getAllRelationshipTypes();
      setRelationship(relationshipsResponse.data.relationship_types || []);

      const triplesResponse = await GraphRepository.getRdfTriples(visibleNodes);
      setTriples(triplesResponse.data.triples || []);
    } catch (error) {
      console.error("Error fetching graph data:", error);
    }
  };

  const handleCreateNode = () => {
    setOpenNodeModal(true);
  };

  const handleCreateRelation = () => {
    setOpenRelationModal(true);
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
  };

  const handleToggleNodeVisibility = (nodeType) => {
    setVisibleNodes(prev => ({
      ...prev,
      [nodeType]: prev[nodeType] === false
    }));
  };

  useEffect(() => {
    GraphRepository.getAllNodeLabels()
      .then(response => {
        setNodes(response?.data?.node_labels)
      })
      .catch(error => console.log(error));
    GraphRepository.getAllRelationshipTypes()
      .then(response => setRelationship(response?.data?.relationship_types))
      .catch(error => console.log(error));
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    function areSetsEqual(setA, setB) {
      if (setA.size !== setB.size) return false;
      for (const item of setA) {
        if (!setB.has(item)) return false;
      }
      return true;
    }

    const nodes = Object.keys(visibleNodes)?.filter(node => visibleNodes[node]);
    GraphRepository.getRdfTriples(nodes, abortController.signal)
      .then(response => {
        const data = response?.data;
        setTriples(data?.triples || []);
        const selectedNodes = new Set(data?.selected_nodes || []);
        const prevSelected = new Set(nodes || []);

        if (!areSetsEqual(selectedNodes, prevSelected)) {
          const visible = {}
          for (const node of data?.available_nodes || []) {
            visible[node] = selectedNodes.has(node);
          }
          setVisibleNodes(visible);
        }
      })
      .catch(error => {
        if (error.name !== 'CanceledError') {
          console.error('Error fetching RDF triples:', error);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [visibleNodes]);

  // Extract unique subjects, predicates, and objects from triples
  const subjectOptions = useMemo(() => {
    const subjects = new Map();
    const typeGroups = new Map();

    triples.forEach(t => {
      if (!subjects.has(t.subject.id)) {
        subjects.set(t.subject.id, {
          id: t.subject.id,
          label: t?.subject?.properties?.searchName || t?.subject?.properties?.name || t.subject.type,
          type: t.subject.type,
          isTypeGroup: false
        });
      }

      // Track types for "All X" options
      if (!typeGroups.has(t.subject.type)) {
        typeGroups.set(t.subject.type, {
          id: `*${t.subject.type}`,
          label: `* ${t.subject.type}`,
          type: t.subject.type,
          isTypeGroup: true
        });
      }
    });

    return [...Array.from(typeGroups.values()), ...Array.from(subjects.values())];
  }, [triples]);

  const predicateOptions = useMemo(() => {
    return [...new Set(triples.map(t => t.predicate.type))];
  }, [triples]);

  const objectOptions = useMemo(() => {
    const objects = new Map();
    const typeGroups = new Map();

    triples.forEach(t => {
      if (!objects.has(t.object.id)) {
        objects.set(t.object.id, {
          id: t.object.id,
          label: t?.object?.properties?.searchName || t?.object?.properties?.name || t.object.type,
          type: t.object.type,
          isTypeGroup: false
        });
      }

      // Track types for "All X" options
      if (!typeGroups.has(t.object.type)) {
        typeGroups.set(t.object.type, {
          id: `*${t.object.type}`,
          label: `* ${t.object.type}`,
          type: t.object.type,
          isTypeGroup: true
        });
      }
    });

    return [...Array.from(typeGroups.values()), ...Array.from(objects.values())];
  }, [triples]);

  // Create stable color mapping for all node types (same logic as in graph-view.js)
  const colorMap = useMemo(() => {
    const map = new Map();

    // Extract unique groups from triples (same as graph-view.js does)
    const allGroups = new Set();
    triples.forEach(t => {
      allGroups.add(t.subject.type);
      allGroups.add(t.object.type);
    });
    const uniqueGroups = [...allGroups];

    // Assign colors to each group by index (matching graph-view.js logic)
    uniqueGroups.forEach((group, index) => {
      const colorObj = nodeColors[index % nodeColors.length];
      map.set(group, colorObj);
    });

    return map;
  }, [triples]);

  // Create a map of node types to their active status
  const nodeActiveStatus = useMemo(() => {
    const statusMap = new Map();

    triples.forEach(t => {
      // Check subject
      if (!statusMap.has(t.subject.type)) {
        const isActive = t.subject.properties?.active !== undefined
          ? (typeof t.subject.properties.active === 'string'
            ? t.subject.properties.active.toLowerCase() === 'true'
            : t.subject.properties.active === true)
          : true; // default to true if no active field
        statusMap.set(t.subject.type, isActive);
      }

      // Check object
      if (!statusMap.has(t.object.type)) {
        const isActive = t.object.properties?.active !== undefined
          ? (typeof t.object.properties.active === 'string'
            ? t.object.properties.active.toLowerCase() === 'true'
            : t.object.properties.active === true)
          : true; // default to true if no active field
        statusMap.set(t.object.type, isActive);
      }
    });

    return statusMap;
  }, [triples]);

  // Filter triples based on selected filters and node visibility
  const filteredTriples = useMemo(() => {
    return triples.filter(t => {
      // Filter out nodes that are hidden
      const isSubjectVisible = visibleNodes[t.subject.type] !== false;
      const isObjectVisible = visibleNodes[t.object.type] !== false;

      if (!isSubjectVisible || !isObjectVisible) {
        return false;
      }

      // When only subject is selected, match it in both subject and object positions
      if (selectedSubject && !selectedPredicate && !selectedObject) {
        const matchAsSubject = selectedSubject.isTypeGroup
          ? t.subject.type === selectedSubject.type
          : t.subject.id === selectedSubject.id;

        const matchAsObject = selectedSubject.isTypeGroup
          ? t.object.type === selectedSubject.type
          : t.object.id === selectedSubject.id;

        return matchAsSubject || matchAsObject;
      }

      // When multiple filters are selected, use standard AND logic
      const matchSubject = !selectedSubject ||
        (selectedSubject.isTypeGroup ? t.subject.type === selectedSubject.type : t.subject.id === selectedSubject.id);

      const matchPredicate = !selectedPredicate || t.predicate.type === selectedPredicate;

      const matchObject = !selectedObject ||
        (selectedObject.isTypeGroup ? t.object.type === selectedObject.type : t.object.id === selectedObject.id);

      return matchSubject && matchPredicate && matchObject;
    });
  }, [triples, selectedSubject, selectedPredicate, selectedObject, visibleNodes]);

  return (
    <Stack direction={"column"} sx={{maxHeight: 'calc(100dvh - 50px)', height: '100%', position: "relative"}}>
      <GraphSearchFilters
        subjectOptions={subjectOptions}
        predicateOptions={predicateOptions}
        objectOptions={objectOptions}
        onSubjectChange={setSelectedSubject}
        onPredicateChange={setSelectedPredicate}
        onObjectChange={setSelectedObject}
        colorMap={colorMap}
      />

      <CreateFabButton
        onCreateSchema={handleCreateSchema}
        onCreateNode={handleCreateNode}
        onCreateRelation={handleCreateRelation}
      />

      <NodeInfoPanel node={selectedNode} onClose={() => setSelectedNode(null)}/>
      {Object.keys(visibleNodes)?.length > 0 ?
        <NodeLegendPanel
          nodes={nodes}
          colorMap={colorMap}
          visibleNodes={visibleNodes}
          nodeActiveStatus={nodeActiveStatus}
          onToggleNode={handleToggleNodeVisibility}
        /> : null}

      <Box id={"graph-view"}
           sx={{maxHeight: "calc(100dvh - 48px)", width: "calc(100vw - 48px)", height: '100%', zIndex: 10}}>
        <GraphView triples={filteredTriples} allTriples={triples} onNodeClick={handleNodeClick}
                   selectedNode={selectedNode} colorMap={colorMap}/>
      </Box>

      <CreateSchemaModal open={openSchemaModal} onClose={() => setOpenSchemaModal(false)}
                         onSuccess={handleSchemaSuccess}/>
      <CreateNodeModal open={openNodeModal} onClose={() => setOpenNodeModal(false)}/>
      <CreateRelationModal open={openRelationModal} onClose={() => setOpenRelationModal(false)}/>
    </Stack>
  )
}